import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useRole } from "@/lib/roles";
import RoleCapabilityMatrix from "@/components/RoleCapabilityMatrix";
import { ShieldCheck, Plus, KeyRound, Lock, Trash2, UserCheck, UserX, Loader2 } from "lucide-react";

const ROLES = ["super-admin", "admin", "tester", "read-only", "disabled"];
const inp = "h-9 border border-[#33424f]/30 px-2 text-xs font-body bg-white focus:border-[#39678f] focus:outline-none";

function badge(s) {
  return {
    active: "bg-[#e6f1df] text-[#1f4715] border-[#2d5e1f]/40",
    inactive: "bg-[#f0ece3] text-[#7a6f57] border-[#33424f]/30",
    suspended: "bg-[#fbf1dc] text-[#6f4d0d] border-[#b9832a]/40",
    archived: "bg-[#fbe4ea] text-[#7a0024] border-[#b30035]/40",
  }[s] || "bg-[#f0ece3] text-[#50493b] border-[#33424f]/30";
}

async function getSudo() {
  const pw = window.prompt("Re-enter your password to confirm this action:");
  if (!pw) throw new Error("cancelled");
  const { data } = await api.post("/admin/reauth", { password: pw });
  return { headers: { "X-Sudo-Token": data.sudo_token } };
}

export default function AdminUsers() {
  const { isSuperAdmin, isAdmin } = useRole();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ username: "", email: "", display_name: "", role: "read-only", reason: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { search, role, status } });
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [search, role, status]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const create = async () => {
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success("User created", { description: `Temporary password: ${data.temporary_password}`, duration: 20000 });
      window.alert(`User created.\n\nTemporary password (share securely, shown once):\n${data.temporary_password}`);
      setShowCreate(false); setForm({ username: "", email: "", display_name: "", role: "read-only", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeRole = async (u) => {
    const nr = window.prompt(`Change role for ${u.display_name}.\nOne of: ${ROLES.join(", ")}`, u.role);
    if (!nr || nr === u.role) return;
    try { const cfg = await getSudo(); await api.patch(`/admin/users/${u.id}`, { role: nr }, cfg); toast.success("Role updated"); load(); }
    catch (e) { if (e.message !== "cancelled") toast.error(formatApiError(e)); }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Send password reset for ${u.display_name}? This signs them out everywhere.`)) return;
    try { const cfg = await getSudo(); const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {}, cfg);
      window.alert(`Temporary password for ${u.display_name} (shown once):\n${data.temporary_password}`); toast.success("Password reset"); load(); }
    catch (e) { if (e.message !== "cancelled") toast.error(formatApiError(e)); }
  };

  const setActive = async (u, activate) => {
    try {
      if (activate) { await api.post(`/admin/users/${u.id}/activate`, {}); }
      else { if (!window.confirm(`Deactivate ${u.display_name}? They will be signed out and blocked from logging in.`)) return;
             const cfg = await getSudo(); await api.post(`/admin/users/${u.id}/deactivate`, {}, cfg); }
      toast.success(activate ? "Activated" : "Deactivated"); load();
    } catch (e) { if (e.message !== "cancelled") toast.error(formatApiError(e)); }
  };

  const setPw = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.display_name} (min 8 characters).\nThey will be signed out of all sessions.`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    try { const cfg = await getSudo(); await api.post(`/admin/users/${u.id}/set-password`, { password: pw }, cfg); toast.success("Password updated"); }
    catch (e) { if (e.message !== "cancelled") toast.error(formatApiError(e)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Archive (soft-delete) ${u.display_name}? Audit history is preserved.`)) return;
    try { const cfg = await getSudo(); await api.delete(`/admin/users/${u.id}`, cfg); toast.success("User archived"); load(); }
    catch (e) { if (e.message !== "cancelled") toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="text-[11px] subtitle tracking-[0.3em] uppercase text-[#39678f] flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Administration</div>
        <Link to="/admin/roles" data-testid="go-roles" className="text-xs subtitle uppercase tracking-wider text-[#39678f] hover:underline">Roles &amp; Permissions →</Link>
      </div>
      <h1 className="font-display text-5xl tracking-tight text-[#33424f] leading-none mb-3">User Management</h1>
      <p className="font-body text-sm text-[#50493b] mb-6 max-w-xl">Create and manage internal application users. Destructive actions require re-authentication and are audited.</p>

      {isAdmin && <RoleCapabilityMatrix />}

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Input data-testid="user-search" placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inp} w-56`} />
        <select data-testid="role-filter" value={role} onChange={(e) => setRole(e.target.value)} className={inp}><option value="all">All roles</option>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select>
        <select data-testid="status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={inp}><option value="all">Any status</option><option value="active">active</option><option value="inactive">inactive</option><option value="suspended">suspended</option><option value="archived">archived</option></select>
        <Button data-testid="create-user-btn" onClick={() => setShowCreate((v) => !v)} className="h-9 bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs ml-auto"><Plus className="w-4 h-4 mr-1" /> Create user</Button>
      </div>

      {showCreate && (
        <div data-testid="create-user-form" className="border border-[#33424f]/20 bg-white p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
          <Field l="Username"><Input data-testid="f-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className={inp} /></Field>
          <Field l="Email"><Input data-testid="f-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></Field>
          <Field l="Display name"><Input data-testid="f-display" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className={inp} /></Field>
          <Field l="Role"><select data-testid="f-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inp}>{ROLES.filter((r) => isSuperAdmin || r !== "super-admin").map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
          <Button data-testid="submit-create-user" onClick={create} className="h-9 bg-[#2d5e1f] hover:bg-[#234a18] text-white subtitle uppercase tracking-wider text-xs">Create</Button>
        </div>
      )}

      <div className="border border-[#33424f]/20 bg-white overflow-x-auto">
        <table className="w-full text-xs data-table min-w-[820px]">
          <thead><tr>{["User", "Email", "Role", "Status", "Last login", "Actions"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
          <tbody data-testid="users-table">
            {loading ? <tr><td colSpan={6} className="px-3 py-8 text-center text-[#50493b]"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            : users.length === 0 ? <tr><td colSpan={6} className="px-3 py-8 text-center text-[#50493b]">No users match.</td></tr>
            : users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-[#33424f]/10">
                <td className="px-3 py-2"><div className="subtitle text-[#33424f]">{u.display_name}</div><div className="text-[10px] text-[#7a6f57]">{u.username}</div></td>
                <td className="px-3 py-2 font-body">{u.email}</td>
                <td className="px-3 py-2 subtitle uppercase text-[10px]">{u.role}</td>
                <td className="px-3 py-2"><span className={`subtitle uppercase text-[10px] px-2 py-0.5 border ${badge(u.status)}`}>{u.status}</span></td>
                <td className="px-3 py-2 font-body text-[#50493b]">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <IconBtn t="Change role" tid={`role-${u.id}`} onClick={() => changeRole(u)}>RL</IconBtn>
                    <IconBtn t="Set password" tid={`setpw-${u.id}`} onClick={() => setPw(u)}><Lock className="w-4 h-4 text-[#39678f]" /></IconBtn>
                    <IconBtn t="Send password reset" tid={`reset-${u.id}`} onClick={() => resetPw(u)}><KeyRound className="w-4 h-4" /></IconBtn>
                    {u.status === "active" ? <IconBtn t="Deactivate" tid={`deact-${u.id}`} onClick={() => setActive(u, false)}><UserX className="w-4 h-4 text-[#b9832a]" /></IconBtn>
                      : <IconBtn t="Activate" tid={`act-${u.id}`} onClick={() => setActive(u, true)}><UserCheck className="w-4 h-4 text-[#2d5e1f]" /></IconBtn>}
                    <IconBtn t="Archive user" tid={`del-${u.id}`} onClick={() => del(u)}><Trash2 className="w-4 h-4 text-[#b30035]" /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ l, children }) { return (<div><div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-1">{l}</div>{children}</div>); }
function IconBtn({ t, tid, onClick, children }) { return (<button title={t} data-testid={tid} onClick={onClick} className="h-8 min-w-8 px-1.5 inline-flex items-center justify-center border border-[#33424f]/20 text-[#33424f] hover:bg-[#f0ece3] text-[10px] subtitle">{children}</button>); }
